/* GH-1133 S5 (design §2.6 "card added mid-preview", §3.9 item 12): the
   read-only "Your first 3 drafts" card on the regular dashboard for a trialing
   org that holds source='preview' items. Without it drafts 2–3 would be
   invisible (today's dashboard knows only one "first post"). No wall CTAs.
   It reads the already-loaded content feed (no extra request) and renders
   null for every other org — so the dashboard is unchanged for them.
   Stays until opened once in this browser (localStorage, a per-viewer
   convenience) or for 7 days after the drafts were written. */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icons.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useDashboardData } from '../../state/DashboardDataContext.jsx';
import { useContentFeedContext } from '../../state/ContentFeedContext.jsx';
import { previewDraftsFromRows } from '../../lib/freePreview.js';
import { isFirstPostOnboarding } from '../../lib/freeOnboarding.js';

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function openedKey(userId) {
  return `dathent.freePreview.carriedOpened.${userId || 'anon'}`;
}

export function carriedDraftsFromFeed(content) {
  const rows = (Array.isArray(content) ? content : []).map((row) => row?.raw || row);
  return previewDraftsFromRows(rows);
}

export default function PreviewCarriedDraftsCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const dash = useDashboardData() || {};
  const { content } = useContentFeedContext();
  const [opened, setOpened] = useState(() => {
    try { return window.localStorage.getItem(openedKey(user?.id)) === '1'; } catch { return false; }
  });
  const trialing = dash.entitlements?.billingStatus === 'trialing' || dash.entitlements?.accessState === 'trialing';
  const firstPostOnly = isFirstPostOnboarding(dash.entitlements);
  const drafts = useMemo(() => (trialing ? carriedDraftsFromFeed(content) : []), [trialing, content]);
  const previewDrafts = drafts.filter((d) => d.source === 'preview');
  const carried = firstPostOnly ? drafts.filter(d => d.source === 'first_post') : previewDrafts;
  if (!trialing || opened || carried.length === 0) return null;
  const newest = Math.max(...carried.map((d) => Date.parse(d.createdAt || '') || 0));
  if (newest && Date.now() - newest > MAX_AGE_MS) return null;

  const open = (draft) => {
    try { window.localStorage.setItem(openedKey(user?.id), '1'); } catch { /* per-viewer convenience */ }
    setOpened(true);
    navigate(`/app/post/${encodeURIComponent(draft.id)}`);
  };

  return (
    <section className="card fpb-carried" aria-labelledby="fpb-carried-title">
      <div className="fpb-carried-head">
        <h3 id="fpb-carried-title" className="fpb-card-h">{t(firstPostOnly ? 'journey.doneTitle' : 'freePreview.carriedTitle')}</h3>
        <p className="fpb-muted">{t(firstPostOnly ? 'firstPostOnboarding.carriedBody' : 'freePreview.carriedBody')}</p>
      </div>
      <ul className="fpb-carried-list">
        {(firstPostOnly ? carried.slice(0, 1) : drafts.slice(0, 3)).map((draft) => (
          <li key={draft.id}>
            <span className="fpb-carried-n">{draft.title}</span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => open(draft)}>
              {t('freePreview.carriedOpen')} <Icon name="arrowRight" size={12} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
